// Simulation and allocation reads/writes, under RLS.
import type { Allocation, ClasseActif, CodeScenario, Simulation } from '@horizon/types';
import { getClient } from './client.js';
import { toErreur, withRetry } from './erreurs.js';

const COLONNES =
  'id,client_id,organisation_id,libelle,statut,horizon_annees,objectif_capital,' +
  'versement_initial,versement_mensuel,jour_prelevement,scenario_defaut,frais_gestion,' +
  'hypotheses_version,date_debut,cree_le,modifie_le';

/** The active simulation of a client, or null when none is active (RG-02). */
export async function simulationActive(clientId: string): Promise<Simulation | null> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('simulations')
      .select(COLONNES)
      .eq('client_id', clientId)
      .eq('statut', 'active')
      .maybeSingle();
    if (error !== null) {
      throw toErreur(error);
    }
    return (data as unknown as Simulation) ?? null;
  });
}

/**
 * The most recently touched active simulation visible to the caller. Opening the
 * simulator without a client lands on it, as the reference screen does.
 */
export async function derniereSimulationActive(): Promise<Simulation | null> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('simulations')
      .select(COLONNES)
      .eq('statut', 'active')
      .order('modifie_le', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error !== null) {
      throw toErreur(error);
    }
    return (data as unknown as Simulation) ?? null;
  });
}

export async function listerSimulations(clientId: string): Promise<readonly Simulation[]> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('simulations')
      .select(COLONNES)
      .eq('client_id', clientId)
      .order('cree_le', { ascending: false });
    if (error !== null) {
      throw toErreur(error);
    }
    return (data ?? []) as unknown as Simulation[];
  });
}

/** Number of active simulations visible to the caller (dashboard counter, RG-02). */
export async function compterSimulationsActives(): Promise<number> {
  return withRetry(async () => {
    const { error, count } = await getClient()
      .from('simulations')
      .select('id', { count: 'exact', head: true })
      .eq('statut', 'active');
    if (error !== null) {
      throw toErreur(error);
    }
    return count ?? 0;
  });
}

/** Active simulations touched since the given date (dashboard "cette semaine" mention). */
export async function compterSimulationsActivesModifieesDepuis(depuis: string): Promise<number> {
  return withRetry(async () => {
    const { error, count } = await getClient()
      .from('simulations')
      .select('id', { count: 'exact', head: true })
      .eq('statut', 'active')
      .gte('modifie_le', depuis);
    if (error !== null) {
      throw toErreur(error);
    }
    return count ?? 0;
  });
}

/** The four allocation rows of a simulation. */
export async function allocations(simulationId: string): Promise<readonly Allocation[]> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('allocations')
      .select('simulation_id,classe,poids')
      .eq('simulation_id', simulationId);
    if (error !== null) {
      throw toErreur(error);
    }
    return (data ?? []) as unknown as Allocation[];
  });
}

/** Asset classes in the reference order, the write order of the four allocation rows. */
const CLASSES_ACTIF: readonly ClasseActif[] = [
  'actions',
  'obligations',
  'fonds_euros',
  'liquidites',
];

/**
 * Upsert the four allocation rows of a simulation (Allocation tab, EF-043).
 * The four rows travel in one PostgREST request, hence one transaction: the
 * deferred RG-04 trigger validates the 100.00 sum at commit, and the write is
 * gated by the allocations RLS policy through the parent simulation. Returns
 * the rows as persisted, so the caller can refresh its state without a re-read.
 */
export async function remplacerAllocations(
  simulationId: string,
  poids: Readonly<Record<ClasseActif, number>>,
): Promise<readonly Allocation[]> {
  const lignes = CLASSES_ACTIF.map((classe) => ({
    simulation_id: simulationId,
    classe,
    poids: poids[classe],
  }));
  const { data, error } = await getClient()
    .from('allocations')
    .upsert(lignes, { onConflict: 'simulation_id,classe' })
    .select('simulation_id,classe,poids');
  if (error !== null) {
    throw toErreur(error);
  }
  return (data ?? []) as unknown as Allocation[];
}

/** Content of the new simulation row (creation modal). Weights travel separately. */
export interface NouvelleSimulation {
  readonly client_id: string;
  readonly organisation_id: string;
  readonly libelle: string;
  readonly horizon_annees: number;
  readonly objectif_capital: number | null;
  readonly versement_initial: number;
  readonly versement_mensuel: number;
  readonly jour_prelevement: number;
  readonly scenario_defaut: CodeScenario;
  /** Organisation default at creation time, read from the organisations table. */
  readonly frais_gestion: number;
  /** Active hypotheses version the projections will be computed with (RG-03). */
  readonly hypotheses_version: number;
  /** Creation day, month zero of the projection. */
  readonly date_debut: string;
}

/**
 * Insert the new simulation as the active one (RG-01). The caller archives the
 * previous active simulation first (RG-02): the unq_simulation_active index
 * would otherwise reject the insert. Writes are gated by the simulations_write
 * RLS policy; the audit_log row is written by the database trigger (RG-15).
 * No retry: a mutation is only sent once.
 */
export async function insererSimulation(entree: NouvelleSimulation): Promise<Simulation> {
  const { data, error } = await getClient()
    .from('simulations')
    .insert({ ...entree, statut: 'active' })
    .select(COLONNES)
    .single();
  if (error !== null) {
    throw toErreur(error);
  }
  return data as unknown as Simulation;
}

/**
 * Archive the active simulation of a client (RG-02). Idempotent: with no active
 * simulation the update matches zero rows, so a retried creation replays safely.
 */
export async function archiverSimulationActive(clientId: string): Promise<void> {
  const { error } = await getClient()
    .from('simulations')
    .update({ statut: 'archivee' })
    .eq('client_id', clientId)
    .eq('statut', 'active');
  if (error !== null) {
    throw toErreur(error);
  }
}

/**
 * Book the validated initial payment on the creation day, as the prototype does
 * when the initial amount is positive. Monthly occurrences are not written here:
 * the generer-occurrences Edge Function owns them (RG-12).
 */
export async function insererVersementInitial(
  simulationId: string,
  montant: number,
  dateIso: string,
): Promise<void> {
  const { error } = await getClient().from('echeances').insert({
    simulation_id: simulationId,
    date_echeance: dateIso,
    type: 'versement_initial',
    montant,
    statut: 'valide',
  });
  if (error !== null) {
    throw toErreur(error);
  }
}

/** One flattened row of the read-only portfolio screen (RG-03 published figures). */
export interface PortefeuilleSynthese {
  readonly simulationId: string;
  readonly clientId: string;
  readonly prenom: string;
  readonly nom: string;
  readonly verse: number;
  readonly capital: number;
  readonly poids: Readonly<Record<ClasseActif, number>>;
}

/** Raw PostgREST shape of a portefeuilles_reference row with its embeds. */
interface LignePortefeuilleBrute {
  readonly simulation_id: string;
  readonly verse: number;
  readonly capital: number;
  readonly simulations: {
    readonly client_id: string;
    readonly clients: { readonly prenom: string; readonly nom: string } | null;
    readonly allocations: readonly { readonly classe: ClasseActif; readonly poids: number }[];
  } | null;
}

/**
 * Portfolio synthesis rows (portfolio screen, RG-03): the published figures of
 * portefeuilles_reference joined in one request to the simulation, its client and
 * its four allocation lines. RLS on simulations restricts the rows to the clients
 * visible to the caller. The seed of migration 0008 assigns simulation ids in the
 * published corpus order, so ordering by id reproduces the reference screen.
 */
export async function listerPortefeuilles(): Promise<readonly PortefeuilleSynthese[]> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('portefeuilles_reference')
      .select(
        'simulation_id,verse,capital,' +
          'simulations(client_id,clients(prenom,nom),allocations(classe,poids))',
      )
      .order('simulation_id', { ascending: true });
    if (error !== null) {
      throw toErreur(error);
    }
    const lignes = (data ?? []) as unknown as readonly LignePortefeuilleBrute[];
    const sortie: PortefeuilleSynthese[] = [];
    for (const ligne of lignes) {
      const sim = ligne.simulations;
      if (sim === null || sim.clients === null) {
        continue;
      }
      const poids: Record<ClasseActif, number> = {
        actions: 0,
        obligations: 0,
        fonds_euros: 0,
        liquidites: 0,
      };
      for (const a of sim.allocations) {
        poids[a.classe] = Number(a.poids);
      }
      sortie.push({
        simulationId: ligne.simulation_id,
        clientId: sim.client_id,
        prenom: sim.clients.prenom,
        nom: sim.clients.nom,
        verse: Number(ligne.verse),
        capital: Number(ligne.capital),
        poids,
      });
    }
    return sortie;
  });
}

/** Active hypotheses of the organisation: scenario code to gross annual return. */
export async function rendementsActifs(organisationId: string): Promise<Record<string, number>> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('hypotheses_scenarios')
      .select('scenario,rendement_brut_annuel,version')
      .eq('organisation_id', organisationId)
      .eq('actif', true);
    if (error !== null) {
      throw toErreur(error);
    }
    const sortie: Record<string, number> = {};
    for (const ligne of data ?? []) {
      sortie[String((ligne as { scenario: string }).scenario)] = Number(
        (ligne as { rendement_brut_annuel: number }).rendement_brut_annuel,
      );
    }
    return sortie;
  });
}
